import React, { useRef, useState } from "react";
import XLSX from "xlsx";
import { Button, IconButton, Modal } from "@mui/material";
import { FaCoins } from "react-icons/fa";

import { removeDiacritics } from "helpers/string";
import dayjs from "dayjs";
import FileSaver from "file-saver";
import ErrorModal from "./Modal/ErrorModal";

function Nfts() {
	const [modalOpen, setModalOpen] = useState(false);
	const [errorCount, setErrorCount] = useState(0);
	const [files, setFiles] = useState<any[]>([]);
	const [exportComponent, setExport] = useState<React.ReactNode>(null);

	const inputFile = useRef<HTMLInputElement>(null);

	const padAndSlice = (str: string, length: number, fill: string = "0") =>
		String(str).padStart(length, fill).substring(0, length);

	const openSelectFile = () => {
		if (inputFile.current) {
			inputFile.current.click();
		}
	};

	const handleOpenModal = count => {
		setErrorCount(count);
		setModalOpen(true);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
	};

	const verificarCamposPreenchidos = (dados, requiredFields: string[]) => {
		const camposUndefined: any[] = [];

		for (let i = 0; i < dados.length; i++) {
			const undefinedFields: any[] = [];

			requiredFields.forEach(field => {
				if (dados[i][field] === undefined) {
					undefinedFields.push(field);
				}
			});

			if (undefinedFields.length > 0) {
				camposUndefined.push({
					index: i + 1,
					colunas: undefinedFields
				});
			}
		}

		return camposUndefined;
	};

	const handleExport = (data: any[]) => {
		const dates = data
			.map(c => dayjs(c["ns1:dhemi"]))
			.sort((a, b) => (a.isAfter(b) ? 1 : -1));

		const txt = [
			padAndSlice("1", 1),
			padAndSlice("1", 3),
			padAndSlice(String(data[0].im), 8),
			padAndSlice(dates.shift()?.format("YYYYMMDD") || "", 8),
			padAndSlice(dates.pop()?.format("YYYYMMDD") || "", 8),
			"\r\n",
			...data.map((c, i) => {
				const {
					"ns1:serie": serie,
					"ns1:nnfse": nnfse,
					"ns1:dhemi": dhemi,
					"ns1:vserv": vserv,
					"codigo servico": codigoServico
				} = c;

				return [
					padAndSlice("4", 1),
					padAndSlice("01", 2),
					padAndSlice(String(serie), 5),
					padAndSlice(String(nnfse), 12),
					padAndSlice(dayjs(dhemi).format("YYYYMMDD"), 8),
					padAndSlice("N", 1),
					padAndSlice("T", 1),
					padAndSlice(
						Number(vserv).toFixed(2).match(/\d/g)?.join("") || "",
						15
					),
					padAndSlice("", 15),
					padAndSlice(codigoServico, 5),
					padAndSlice("", 4),
					padAndSlice("0", 4),
					padAndSlice("2", 1),
					padAndSlice("2", 1),
					padAndSlice(
						String(c["ns1:cnpj" || ""])
							.match(/\d/g)
							?.join("") || "",
						14
					),
					padAndSlice("", 8),
					padAndSlice(c["ns1:xnome"], 75, " "),
					padAndSlice("Rua", 3, " "),
					padAndSlice(
						removeDiacritics(c["ns1:xlgr"]).replace("(", "").replace(")", ""),
						50,
						" "
					),
					padAndSlice(String(c["ns1:nro"]), 10, " "),
					padAndSlice("", 30, " "),
					padAndSlice(c["ns1:xbairro"], 30, " "),
					padAndSlice(removeDiacritics(c["ns1:xlocemi"]), 50, " "),
					padAndSlice(c["ns1:uf"], 2, " "),
					padAndSlice(
						String(c["ns1:cep" || ""])
							.match(/\d/g)
							?.join("") || "",
						8
					),
					padAndSlice("", 75, " "),
					padAndSlice("1", 1),
					padAndSlice("5", 1),
					padAndSlice("", 8, " "),
					padAndSlice(c["ns1:xdescserv"], 1000, " "),
					"\r\n"
				].join("");
			}),
			padAndSlice("9", 1),
			padAndSlice(String(data.length), 7),
			padAndSlice(
				String(
					data
						.reduce((a, b) => a + Number(b["ns1:vserv"]), 0)
						.toFixed(2)
						.match(/\d/g)
						.join("")
				),
				15
			),
			padAndSlice("0", 15),
			"\r\n"
		].join("");

		const blob = new Blob([txt], { type: "text/plain;charset=utf-8" });

		FileSaver.saveAs(blob, `Nfts_${dayjs().format("YYYYMMDDHHmmss")}.txt`);
	};

	const handleFileSelected = async e => {
		const file = e.target.files[0];

		if (!file) return;

		e.target.value = null;

		const reader = new FileReader();
		const rABS = !!reader.readAsBinaryString;

		reader.onload = async e => {
			try {
				/* Parse data */
				const bstr = e.target?.result;
				const wb = XLSX.read(bstr, { type: rABS ? "binary" : "array" });
				/* Get first worksheet */
				const wsname = wb.SheetNames[0];
				const sheet = wb.Sheets[wsname];

				const ref = XLSX.utils.decode_range(sheet["!ref"] || "");

				for (let C = ref.s.c; C <= ref.e.c; ++C) {
					const cell = sheet[XLSX.utils.encode_cell({ r: ref.s.r, c: C })];

					if (cell && cell.t === "s") {
						cell.v = removeDiacritics(cell.v.trim().toLowerCase());

						if (cell.w) cell.w = removeDiacritics(cell.w.trim().toLowerCase());
					}
				}

				const rawData = XLSX.utils.sheet_to_json(sheet);

				const requiredFields = [
					"ns1:dhemi",
					"im",
					"ns1:serie",
					"ns1:nnfse",
					"ns1:vserv",
					"codigo servico",
					"ns1:cnpj",
					"ns1:xnome",
					"ns1:xlgr",
					"ns1:nro",
					"ns1:xbairro",
					"ns1:xlocemi",
					"ns1:uf",
					"ns1:cep",
					"ns1:xdescserv"
				];

				const validationResults = verificarCamposPreenchidos(
					rawData,
					requiredFields
				);

				const filesWithUndefinedFields = validationResults.filter(
					result => result.colunas.length > 0
				);

				const totalErrors = filesWithUndefinedFields.length;

				if (totalErrors > 0) {
					console.error(
						`Total de ${totalErrors} arquivos com campos indefinidos.`
					);

					filesWithUndefinedFields.forEach(file => {
						console.error(
							`Arquivo ${
								file.index
							} com campos indefinidos: ${file.colunas.join(", ")}`
						);
					});

					setFiles(filesWithUndefinedFields);

					setErrorCount(totalErrors);
					handleOpenModal(totalErrors);
				} else {
					const data = rawData.map(entry => {
						return entry;
					});

					handleExport(data);
				}
			} catch (error) {
				console.error("Erro ao processar o arquivo:", error);
			}
		};

		if (rABS) reader.readAsBinaryString(file);
		else reader.readAsArrayBuffer(file);

		if (inputFile.current) inputFile.current.value = "";
	};

	return (
		<>
			<IconButton onClick={openSelectFile}>
				<FaCoins />
			</IconButton>
			<input
				ref={inputFile}
				style={{ display: "none" }}
				type="file"
				accept="application/excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
				onChange={handleFileSelected}
			/>
			{exportComponent}
			<ErrorModal
				filesUndefined={files}
				open={modalOpen}
				count={errorCount}
				onClose={handleCloseModal}
			/>
		</>
	);
}

export default Nfts;
